import { useState, useEffect, useMemo } from 'react'
import styled from '@emotion/styled'
import { csv } from 'd3-fetch'
import { Theme } from '../../styles/settings/theme'
import { MediaQueries as Mq } from '../../styles/settings/theme'

import { Flex, Content } from '../atoms/Basics'
import ChartTitle from '../atoms/ChartTitle'
import ChartContainer from '../atoms/ChartContainer'
import PercentTickAxis from '../atoms/PercentTickAxis'
import LegendColors from '../atoms/LegendColors'
import Subtitle from '../atoms/Subtitle'
import Note from '../atoms/Note'
import YearSelector from '../atoms/yearSelector'

const RowFlex = styled(Flex)`
  align-items: center;
  margin: 0 1rem 0.75rem;
`
const Label = styled.div`
  color: ${(props) => props.theme.colors.gray};
  margin: 0 1rem 0 0;
  //min-width: 16rem;
  width: 17rem;
  font-size: ${(props) => props.theme.fontSizes[0]};
  font-weight: 600;
  text-align: right;
  ${Mq.md} {
    font-size: 12px;
    width: 11rem;
  }
  ${Mq.sm} {
    font-size: 11px;
    width: 9rem;
  }
  ${Mq.xs} {
    font-size: 10px;
    width: 7.5rem;
  }
`
const BaseBar = styled.div`
  flex: 1;
  height: 2.25rem;
  background: ${(props) => props.gradient || props.theme.colors.gray0};
  position: relative;
`

const TickContainer = styled.div`
  flex: 1;
  position: relative;
  height: 7rem;
`

const Opportunities = ({ note }) => {
  const $ = window.WILDLABS_VARS
  const [allData, setAllData] = useState(null)
  const [data, setData] = useState(null)
  const [availableYears, setAvailableYears] = useState([])
  const [selectedYear, setSelectedYear] = useState('All Years')

  const rankingColumns = useMemo(
    () => [
      $.opportunities_columns.First,
      $.opportunities_columns.Second,
      $.opportunities_columns.Third,
      $.opportunities_columns.Fourth,
      $.opportunities_columns.Fifth,
      $.opportunities_columns.Sixth,
      $.opportunities_columns.Seventh,
      $.opportunities_columns.Eighth,
      $.opportunities_columns.Ninth,
      $.opportunities_columns.Tenth
    ],
    [$]
  )

  const allBars = useMemo(
    () => [
      $.opportunities_columns.First,
      $.opportunities_columns.Second,
      $.opportunities_columns.Third,
      $.opportunities_columns.Fourth,
      $.opportunities_columns.Fifth,
      $.opportunities_columns.Sixth,
      $.opportunities_columns.Seventh,
      $.opportunities_columns.Eighth,
      $.opportunities_columns.Ninth
    ],
    [$]
  )

  const colors = [
    Theme.colors.darkGreen,
    Theme.colors.green,
    Theme.colors.lightGreen,
    Theme.colors.gray5,
    Theme.colors.gray4,
    Theme.colors.gray3,
    Theme.colors.gray2,
    Theme.colors.gray1,
    Theme.colors.gray0
  ]

  useEffect(() => {
    csv(process.env.PUBLIC_URL + '/data/opportunities.csv').then((rawData) => {
      setAllData(rawData)

      // Extract unique years from the data
      const years = [...new Set(rawData.map((row) => row.year))].sort(
        (a, b) => {
          if (a === 'All Years') return -1
          if (b === 'All Years') return 1
          return a.localeCompare(b)
        }
      )
      setAvailableYears(years)
    })
  }, [])

  useEffect(() => {
    if (allData && selectedYear) {
      const filteredData = allData.filter((row) => {
        // Filter by selected year
        if (row.year !== selectedYear) return false

        const hasNAValues = rankingColumns.some(
          (col) =>
            row[col] === 'NA' || row[col] === '' || row[col] === undefined
        )

        return !hasNAValues
      })

      setData(filteredData)
    }
  }, [allData, selectedYear, rankingColumns])

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value)
  }

  const dataSorted = data?.sort((a, b) => {
    return (
      parseFloat(a[$.opportunities_columns.Tenth]) -
      parseFloat(b[$.opportunities_columns.Tenth])
    )
  })

  return (
    <ChartContainer>
      <ChartTitle text={$.opportunities_titles.chartTitle}>
        <YearSelector value={selectedYear} onChange={handleYearChange}>
          {availableYears.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </YearSelector>
      </ChartTitle>
      <Content flexDirection="column">
        <RowFlex justifyContent="space-between">
          <LegendColors colors={colors} columns={allBars} />
        </RowFlex>
        <RowFlex>
          <Label></Label>
          <TickContainer>
            <Flex justifyContent="center">
              <Subtitle
                text={$.opportunities_titles.subtitle}
                size={Theme.fontSizes[2]}
                color={Theme.colors.gray}
                wide={true}
              ></Subtitle>
            </Flex>
            <PercentTickAxis />
          </TickContainer>
        </RowFlex>
        {dataSorted?.map((row, i) => {
          // Create CSS gradient string for seamless color transitions
          const gradientParts = []
          let cumulativePosition = 0

          allBars.forEach((column, segmentIndex) => {
            const rawWidth = parseFloat(row[column]) || 0
            const widthPercent = rawWidth * 100

            if (widthPercent > 0) {
              const startPercent = cumulativePosition
              const endPercent = cumulativePosition + widthPercent

              // Add color stop at start and end positions
              gradientParts.push(
                `${colors[segmentIndex]} ${startPercent.toFixed(2)}%`
              )
              gradientParts.push(
                `${colors[segmentIndex]} ${endPercent.toFixed(2)}%`
              )

              cumulativePosition += widthPercent
            }
          })

          // Create linear gradient string
          const gradient =
            gradientParts.length > 0
              ? `linear-gradient(to right, ${gradientParts.join(', ')})`
              : Theme.colors.gray0

          return (
            <RowFlex key={`${row.opportunity}-${i}`}>
              <Label>{row.opportunity}</Label>
              <BaseBar gradient={gradient} />
            </RowFlex>
          )
        })}
      </Content>
      <Note note={note} />
    </ChartContainer>
  )
}

export default Opportunities
